/**
 * GraphQL utilities and client configuration for MercNet ecosystem
 * Updated to use GraphQL Code Generator with traditional SDK approach
 */

import { GraphQLClient } from 'graphql-request'
import type { Variables } from 'graphql-request'
import type { GraphQLResponse, GraphQLError, Market } from '../types/index.js'
import { MercNetError } from '../utils/index.js'
import {
  getSdk,
  type Sdk,
  type GetAllMarketsQuery,
  type GetMarketByIdQuery,
  type GetMarketByTickerQuery,
  type GetMarketsByStatusQuery,
  type GetAllMarketsQueryVariables,
  type GetMarketByIdQueryVariables,
  type GetMarketByTickerQueryVariables,
  type GetMarketsByStatusQueryVariables
} from './generated.js'

// GraphQL Client configuration
export interface GraphQLClientConfig {
  endpoint: string
  headers?: Record<string, string>
  retries?: number
  debug?: boolean
}

// Default configuration
const DEFAULT_CONFIG: Partial<GraphQLClientConfig> = {
  retries: 3,
  debug: false
}

// Custom GraphQL client wrapper
export class MercNetGraphQLClient {
  private client: GraphQLClient
  private config: GraphQLClientConfig
  private readonly sdk: Sdk

  constructor(config: GraphQLClientConfig) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.client = new GraphQLClient(this.config.endpoint, {
      headers: this.config.headers
    })

    // Initialize SDK with the GraphQL client
    this.sdk = getSdk(this.client)
  }

  // Update headers (useful for authentication)
  setHeaders(headers: Record<string, string>): void {
    this.client.setHeaders(headers)
  }

  // Add a single header
  setHeader(key: string, value: string): void {
    this.client.setHeader(key, value)
  }

  // Execute a GraphQL query
  async query<T = any, V extends Variables = Variables>(
    query: string,
    variables?: V,
    requestHeaders?: Record<string, string>
  ): Promise<T> {
    try {
      if (this.config.debug) {
        console.log('[GraphQL Query]', { query, variables })
      }

      const response = await this.client.request<T>(query, variables, requestHeaders)

      if (this.config.debug) {
        console.log('[GraphQL Response]', response)
      }

      return response
    } catch (error) {
      if (this.config.debug) {
        console.error('[GraphQL Error]', error)
      }

      throw this.handleGraphQLError(error)
    }
  }

  // Execute a GraphQL mutation
  async mutate<T = any, V extends Variables = Variables>(
    mutation: string,
    variables?: V,
    requestHeaders?: Record<string, string>
  ): Promise<T> {
    return this.query<T, V>(mutation, variables, requestHeaders)
  }

  // Execute raw request with full response
  async rawRequest<T = any, V extends Variables = Variables>(
    query: string,
    variables?: V,
    requestHeaders?: Record<string, string>
  ): Promise<GraphQLResponse<T>> {
    try {
      const response = await this.client.rawRequest<T>(query, variables, requestHeaders)
      return {
        data: response.data,
        errors: response.errors as any[]
      }
    } catch (error) {
      throw this.handleGraphQLError(error)
    }
  }

  // Typed methods for market operations using generated SDK
  public async getAllMarkets(): Promise<Market[]> {
    try {
      if (this.config.debug) {
        console.log('[GraphQL] Fetching all markets')
      }

      const response = await this.sdk.GetAllMarkets()
      const markets = this.transformMarketsResponse(response)

      if (this.config.debug) {
        console.log('[GraphQL] Retrieved markets:', markets.length)
      }

      return markets
    } catch (error) {
      throw this.handleGraphQLError(error)
    }
  }

  public async getMarketById(id: number): Promise<Market | null> {
    try {
      if (this.config.debug) {
        console.log('[GraphQL] Fetching market by ID:', id)
      }

      const response = await this.sdk.GetMarketById({ id })
      const markets = this.transformMarketsResponse(response)

      const market = markets.length > 0 ? markets[0] : null

      if (this.config.debug) {
        console.log('[GraphQL] Retrieved market:', market ? market.id : 'not found')
      }

      return market
    } catch (error) {
      throw this.handleGraphQLError(error)
    }
  }

  public async getMarketByTicker(ticker: string): Promise<Market | null> {
    try {
      if (this.config.debug) {
        console.log('[GraphQL] Fetching market by ticker:', ticker)
      }

      const response = await this.sdk.GetMarketByTicker({ ticker })
      const markets = this.transformMarketsResponse(response)

      const market = markets.length > 0 ? markets[0] : null

      if (this.config.debug) {
        console.log('[GraphQL] Retrieved market by ticker:', market ? market.ticker : 'not found')
      }

      return market
    } catch (error) {
      throw this.handleGraphQLError(error)
    }
  }

  public async getMarketsByStatus(status: string): Promise<Market[]> {
    try {
      if (this.config.debug) {
        console.log('[GraphQL] Fetching markets by status:', status)
      }

      const response = await this.sdk.GetMarketsByStatus({ status })
      const markets = this.transformMarketsResponse(response)

      if (this.config.debug) {
        console.log('[GraphQL] Retrieved markets by status:', markets.length)
      }

      return markets
    } catch (error) {
      throw this.handleGraphQLError(error)
    }
  }

  // Transform GraphQL response to Market array
  private transformMarketsResponse(
    response:
      | GetAllMarketsQuery
      | GetMarketByIdQuery
      | GetMarketByTickerQuery
      | GetMarketsByStatusQuery
  ): Market[] {
    if (!response.marketCollection?.edges) {
      return []
    }

    return response.marketCollection.edges
      .filter(edge => edge?.node) // Filter out null/undefined nodes
      .map(edge => {
        const node = edge.node!
        return {
          id: node.id,
          name: node.name,
          short_name: node.short_name,
          ticker: node.ticker,
          price: node.price,
          type: node.type,
          created_at: node.created_at,
          last_update_at: node.last_update_at,
          trend_status: node.trend_status,
          sentiment: node.sentiment,
          sentiment_update_at: node.sentiment_update_at,
          market_activity_score_1h: node.market_activity_score_1h,
          market_activity_score_1d: node.market_activity_score_1d,
          data: node.data,
          status: node.status
        }
      })
  }

  // Handle GraphQL errors
  private handleGraphQLError(error: any): MercNetError {
    if (error.response?.errors) {
      const graphqlErrors = error.response.errors as GraphQLError[]
      const message = graphqlErrors.map(e => e.message).join(', ')
      return new MercNetError(message, 'GRAPHQL_ERROR', {
        errors: graphqlErrors,
        query: error.request?.query,
        variables: error.request?.variables
      })
    }

    if (error.message) {
      return new MercNetError(error.message, 'GRAPHQL_NETWORK_ERROR', {
        originalError: error
      })
    }

    return new MercNetError('Unknown GraphQL error', 'GRAPHQL_UNKNOWN_ERROR', {
      originalError: error
    })
  }
}

// Factory function to create a GraphQL client
export const createGraphQLClient = (config: GraphQLClientConfig): MercNetGraphQLClient => {
  return new MercNetGraphQLClient(config)
}

// Common GraphQL fragments
export const COMMON_FRAGMENTS = {
  // Market fragment
  MARKET_FRAGMENT: `
    fragment MarketFragment on market {
      id
      name
      short_name
      ticker
      price
      type
      created_at
      last_update_at
      trend_status
      sentiment
      sentiment_update_at
      market_activity_score_1h
      market_activity_score_1d
      data
      status
    }
  `,

  // Pagination info fragment
  PAGINATION_INFO: `
    fragment PaginationInfo on PageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  `,

  // Error fragment
  ERROR_INFO: `
    fragment ErrorInfo on Error {
      message
      code
      details
    }
  `
}

// GraphQL query builder helpers
export class QueryBuilder {
  private fields: string[] = []
  private fragments: string[] = []
  private variables: string[] = []

  // Add a field to the query
  field(name: string, subFields?: string[]): this {
    if (subFields) {
      this.fields.push(`${name} { ${subFields.join(' ')} }`)
    } else {
      this.fields.push(name)
    }
    return this
  }

  // Add a fragment to the query
  fragment(name: string, fragment: string): this {
    this.fragments.push(fragment)
    this.fields.push(`...${name}`)
    return this
  }

  // Add a variable to the query
  variable(name: string, type: string): this {
    this.variables.push(`$${name}: ${type}`)
    return this
  }

  // Build the final query
  build(operationType: 'query' | 'mutation' = 'query', operationName?: string): string {
    const variablesPart = this.variables.length > 0 ? `(${this.variables.join(', ')})` : ''
    const operationPart = operationName
      ? `${operationType} ${operationName}${variablesPart}`
      : `${operationType}${variablesPart}`

    const query = `
      ${operationPart} {
        ${this.fields.join('\n')}
      }
      ${this.fragments.join('\n')}
    `

    return query.trim()
  }
}

// Helper to create a query builder
export const createQueryBuilder = (): QueryBuilder => {
  return new QueryBuilder()
}

// Utility functions for GraphQL operations
export const gql = (strings: TemplateStringsArray, ...values: any[]): string => {
  return strings.reduce((result, string, index) => {
    return result + string + (values[index] || '')
  }, '')
}

// GraphQL schema utilities
export interface GraphQLSchemaInfo {
  types: string[]
  queries: string[]
  mutations: string[]
  subscriptions: string[]
}

export const getSchemaInfo = async (client: MercNetGraphQLClient): Promise<GraphQLSchemaInfo> => {
  const introspectionQuery = gql`
    query IntrospectionQuery {
      __schema {
        types {
          name
          kind
        }
        queryType {
          fields {
            name
          }
        }
        mutationType {
          fields {
            name
          }
        }
        subscriptionType {
          fields {
            name
          }
        }
      }
    }
  `

  const response = await client.query<any>(introspectionQuery)
  const schema = response.__schema

  return {
    types: schema.types.map((type: any) => type.name),
    queries: schema.queryType?.fields?.map((field: any) => field.name) || [],
    mutations: schema.mutationType?.fields?.map((field: any) => field.name) || [],
    subscriptions: schema.subscriptionType?.fields?.map((field: any) => field.name) || []
  }
}

// Re-export types for convenience
export type {
  GetAllMarketsQuery,
  GetMarketByIdQuery,
  GetMarketByTickerQuery,
  GetMarketsByStatusQuery,
  GetAllMarketsQueryVariables,
  GetMarketByIdQueryVariables,
  GetMarketByTickerQueryVariables,
  GetMarketsByStatusQueryVariables,
  Sdk
} from './generated.js'
